import React, { useState, useLayoutEffect, useMemo, useCallback } from 'react';
import {
    VStack,
    HStack,
    MediaGallery,
    MediaGalleryThumbnails,
    FileItem,
    ImageItem,
    Box,
    Text,
    Divider,
} from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { AttachmentDto, UsageActionEnum } from '@mark43/cad-api';
import { useAttachmentUsageLog } from '../usage-logs/hooks/use-attachment-usage-log';
import { useAttachments } from './hooks/use-attachments';
import { useDeleteAttachment } from './hooks/use-delete-attachment';
import { getFileName, convertAttachmentsToCombinedItems, sortAttachments } from './helpers';
import { CombinedMediaItem, AttachmentSortOptionValue, AttachmentsPanelType } from './types';
import { CustomMediaItemViewer } from './custom-media-item-viewer';
import { AttachmentInfoSection } from './attachment-info-section';
import { DEFAULT_ATTACHMENT_SORT_OPTION_VALUE } from './constants';
import { useAttachmentsAbilities } from './hooks/use-attachments-abilities';
import { useAttachmentSizeLimits } from './hooks/use-attachment-size-limits';
import { LoadingIndicator } from '../common/components/loading-indicator';

export interface AttachmentsPreviewProps {
    entityId: string;
    entityType: AttachmentsPanelType;
    entityNumber?: string;
    attachmentId?: number;
    onSelectedAttachmentChange?: (name: string) => void;
    onOpenEventPanel?: () => void;
    onOpenBoloPanel?: () => void;
    sortOption?: AttachmentSortOptionValue;
    isReadOnly?: boolean;
}

export const AttachmentsPreview = observer(function AttachmentsPreview({
    entityId,
    entityType,
    entityNumber,
    attachmentId,
    onSelectedAttachmentChange,
    onOpenEventPanel,
    onOpenBoloPanel,
    sortOption = DEFAULT_ATTACHMENT_SORT_OPTION_VALUE,
    isReadOnly = false,
}: AttachmentsPreviewProps): React.ReactElement {
    const { logAttachmentAction } = useAttachmentUsageLog({
        entityId: Number(entityId),
        entityType,
        entityNumber,
    });

    const { data: attachmentsData, isLoading: isAttachmentsLoading } = useAttachments({
        entityId: Number(entityId),
        entityType,
        // Avoid an unnecessary `get_attachments` call when opening the preview panel:
        // the attachments list is typically already cached from the Event “Attachments” tab.
        // If that tab was never opened, the query will fetch normally anyway—so we don’t need
        // an extra refetch on mount.
        refetchOnMount: false,
    });
    const { canDeleteAttachments } = useAttachmentsAbilities(entityType);
    const mergedAttachments = attachmentsData?.mergedAttachments;
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const {
        imageMaxPreviewSizeBytes,
        pdfMaxPreviewSizeBytes,
        videoMaxPreviewSizeBytes,
        thumbnailMaxSizeBytes,
    } = useAttachmentSizeLimits();
    const deleteAttachmentMutation = useDeleteAttachment({
        entityId: Number(entityId),
        entityType,
    });

    const handleDeleteAttachment = useCallback(
        async function handleDelete(
            attachmentId: number,
            attachmentType: AttachmentDto['attachmentType']
        ) {
            await deleteAttachmentMutation.mutateAsync({
                attachmentId,
                attachmentType,
                entityId: Number(entityId),
            });
        },
        [deleteAttachmentMutation, entityId]
    );

    // Sort attachments based on the selected sort option
    const sortedAttachments = useMemo(() => {
        // attachments and mergedAttachments will appear in two separate sections so they are sorted separately
        return [
            ...sortAttachments(attachmentsData?.attachments || [], sortOption),
            ...sortAttachments(attachmentsData?.mergedAttachments || [], sortOption),
        ];
    }, [attachmentsData, sortOption]);

    const combinedItems = useMemo<CombinedMediaItem[]>(
        () =>
            convertAttachmentsToCombinedItems({
                attachments: sortedAttachments,
                previewSizeLimits: {
                    imageMaxPreviewSizeBytes,
                    pdfMaxPreviewSizeBytes,
                    videoMaxPreviewSizeBytes,
                    thumbnailMaxSizeBytes,
                },
            }),
        [
            sortedAttachments,
            imageMaxPreviewSizeBytes,
            pdfMaxPreviewSizeBytes,
            videoMaxPreviewSizeBytes,
            thumbnailMaxSizeBytes,
        ]
    );

    const selectedAttachment = useMemo(
        () =>
            sortedAttachments?.find((attachment) => {
                return attachment.attachmentId.toString() === selectedId;
            }),
        [sortedAttachments, selectedId]
    );

    const isSelectedMergedAttachment = useMemo(() => {
        if (!mergedAttachments?.length || !selectedAttachment) return false;

        return !!mergedAttachments.find(
            (attachment) => attachment.attachmentId === selectedAttachment.attachmentId
        );
    }, [selectedAttachment, mergedAttachments]);

    const handleSelectedChange = (id: string, fileName?: string) => {
        if (id === selectedId) return;
        setSelectedId(id);

        let resolvedFileName = fileName;
        if (!resolvedFileName) {
            const attachment = sortedAttachments?.find((att) => att.attachmentId.toString() === id);
            if (attachment) {
                resolvedFileName = getFileName(attachment);
            }
        }

        if (resolvedFileName) {
            onSelectedAttachmentChange?.(resolvedFileName);
            logAttachmentAction(
                UsageActionEnum.VIEWED_ATTACHMENT.name,
                Number(id),
                resolvedFileName
            );
        }
    };

    const showingAttachmentPreview = !!selectedId;

    useLayoutEffect(
        function initializeSelectedAttachment() {
            if (combinedItems.length) {
                const attachmentIdString = attachmentId?.toString();
                const initialItem = attachmentId
                    ? combinedItems.find((item) => item.id === attachmentIdString)
                    : combinedItems[0];

                if (initialItem) {
                    handleSelectedChange(initialItem.id, initialItem.title || 'Untitled');
                }
            }
        },
        // attachmentId drives selection when clicking a thumbnail from the attachments tab;
        // !!combinedItems.length handles case where preview panel was saved to workspace and opens automatically on refresh where data arrives after mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [attachmentId, !!combinedItems.length]
    );

    if (isAttachmentsLoading) {
        return <LoadingIndicator />;
    }

    if (!sortedAttachments?.length) {
        return <Text>No attachments found</Text>;
    }

    // Custom thumbnail renderer using Arc FileItem
    const renderThumbnail = (
        item: ImageItem,
        isActive: boolean,
        itemProps: Pick<React.HTMLAttributes<HTMLDivElement>, 'role' | 'tabIndex' | 'onClick'>
    ) => {
        return (
            <FileItem
                isActive={isActive}
                variant="thumbnail"
                fileType={item.type}
                fileName={item.title || 'Untitled'}
                thumbnailSrc={item.thumbnailSrc ?? item.src}
                size="md"
                {...itemProps}
            />
        );
    };

    return (
        <Box
            width="100%"
            height="100%"
            minH={0}
            sx={{
                // overriding arc default styles that was applying unnecessary margin-left
                mt: '0px !important',
            }}
        >
            <HStack
                align="start"
                width="100%"
                height="100%"
                bgColor="var(--arc-colors-surface-canvas)"
            >
                <VStack spacing={4} flex={1} height="100%" minW={0}>
                    <Box
                        position="relative"
                        width="100%"
                        display="flex"
                        flexDir="column"
                        flex={1}
                        minH={0}
                    >
                        <MediaGallery
                            items={combinedItems}
                            selectedId={selectedId || undefined}
                            onSelectedChange={handleSelectedChange}
                            loop={true}
                        >
                            <Box flex={1} overflow="hidden" display="flex" alignItems="flex-start">
                                <CustomMediaItemViewer
                                    combinedItems={combinedItems}
                                    selectedId={selectedId}
                                />
                            </Box>
                            <MediaGalleryThumbnails renderThumbnail={renderThumbnail} />
                        </MediaGallery>
                        {/* Counter overlay positioned dynamically based on content type */}
                        {(() => {
                            return (
                                <Box
                                    position="absolute"
                                    bottom="120px"
                                    left="50%"
                                    transform="translateX(-50%)"
                                    backgroundColor="var(--arc-colors-background-overlayShim)"
                                    color="white"
                                    px="10px"
                                    py="4px"
                                    borderRadius="12px"
                                    fontSize="11px"
                                    fontWeight="medium"
                                    zIndex="var(--arc-zIndex-overlay)"
                                    pointerEvents="none"
                                    textAlign="center"
                                    whiteSpace="nowrap"
                                >
                                    {combinedItems.findIndex((item) => item.id === selectedId) + 1}{' '}
                                    of {combinedItems.length}
                                </Box>
                            );
                        })()}
                    </Box>
                </VStack>
                {showingAttachmentPreview && <Divider orientation="vertical" />}
                <AttachmentInfoSection
                    selectedAttachment={selectedAttachment}
                    entityId={Number(entityId)}
                    entityType={entityType}
                    entityNumber={entityNumber}
                    onOpenEventPanel={onOpenEventPanel}
                    onOpenBoloPanel={onOpenBoloPanel}
                    onDeleteAttachment={handleDeleteAttachment}
                    isDeleting={deleteAttachmentMutation.isLoading}
                    hideDelete={
                        isReadOnly || isSelectedMergedAttachment || !canDeleteAttachments
                    }
                    isEditable={!isReadOnly}
                />
            </HStack>
        </Box>
    );
});
